import { useCallback, useEffect, useState } from 'react'
import {
  getBotStats,
  listBots,
  type BotStats,
  type BotListItem,
  type BotStatus,
} from '../../api/admin'
import {
  tokens,
  styles,
  Kpi,
  Badge,
  SearchInput,
  PillGroup,
  EmptyState,
  fmtNum,
  fmtRelative,
  type BadgeTone,
} from './_ui'

// ── Status metadata ──────────────────────────────────────────────────────────

const STATUS_TONE: Record<string, BadgeTone> = {
  ACTIVE: 'green',
  PENDING: 'amber',
  REVOKED: 'default',
  FAILED: 'red',
}

const STATUS_ORDER: BotStatus[] = ['ACTIVE', 'PENDING', 'REVOKED', 'FAILED']

// ── Sparkline (compact inline SVG) ───────────────────────────────────────────

function Sparkline({
  data,
  height = 48,
  color = tokens.accent,
}: {
  data: { date: string; new_bots: number }[]
  height?: number
  color?: string
}) {
  if (data.length < 2) return null
  const w = 100 // viewBox width; scales to container
  const max = Math.max(...data.map((d) => d.new_bots), 1)
  const x = (i: number) => (i / (data.length - 1)) * w
  const y = (v: number) => height - (v / max) * (height - 6) - 3
  const line = data
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(d.new_bots).toFixed(1)}`)
    .join(' ')
  const area = `${line} L ${w} ${height} L 0 ${height} Z`
  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${w} ${height}`}
      preserveAspectRatio="none"
      style={{ display: 'block' }}
    >
      <path d={area} fill={color} opacity={0.12} />
      <path
        d={line}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

// ── Status breakdown bar ─────────────────────────────────────────────────────

function StatusBreakdown({ byStatus }: { byStatus: Record<string, number> }) {
  const total = Object.values(byStatus).reduce((a, b) => a + b, 0)
  if (total === 0) return null
  const TONE_COLOR: Record<string, string> = {
    ACTIVE: tokens.green,
    PENDING: tokens.amber,
    REVOKED: '#9ca3af',
    FAILED: tokens.red,
  }
  const segs = STATUS_ORDER.filter((s) => (byStatus[s] || 0) > 0)
  return (
    <div>
      <div
        style={{
          display: 'flex',
          height: 10,
          borderRadius: 6,
          overflow: 'hidden',
          background: tokens.pageBg,
        }}
      >
        {segs.map((s) => (
          <div
            key={s}
            title={`${s}: ${byStatus[s]}`}
            style={{ width: `${((byStatus[s] || 0) / total) * 100}%`, background: TONE_COLOR[s] }}
          />
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 10 }}>
        {segs.map((s) => (
          <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 99,
                background: TONE_COLOR[s],
                display: 'inline-block',
              }}
            />
            <span style={{ ...styles.text, fontSize: 12, fontWeight: 600 }}>{byStatus[s]}</span>
            <span style={{ ...styles.subtle, fontSize: 11 }}>{s.toLowerCase()}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Bot row ──────────────────────────────────────────────────────────────────

function BotRow({ bot }: { bot: BotListItem }) {
  const tone = bot.status ? STATUS_TONE[bot.status] ?? 'default' : 'default'
  return (
    <div style={{ ...styles.card, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ ...styles.text, fontSize: 14, fontWeight: 600 }}>
              {bot.bot_username ? `@${bot.bot_username}` : '— no username yet —'}
            </span>
            {bot.status && <Badge tone={tone}>{bot.status.toLowerCase()}</Badge>}
            <Badge tone={bot.is_managed ? 'purple' : 'teal'}>
              {bot.is_managed ? 'managed' : 'own token'}
            </Badge>
          </div>
          <div style={{ ...styles.subtle, fontSize: 12, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            🏪 {bot.merchant_name || '— unknown shop —'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
            {bot.channel_id ? (
              <Badge tone="blue" icon="📢">
                {bot.channel_title || (bot.channel_username ? `@${bot.channel_username}` : 'channel linked')}
              </Badge>
            ) : (
              <span style={{ ...styles.faint, fontSize: 11 }}>no channel linked</span>
            )}
            {bot.candidate_count > 0 && (
              <span style={{ ...styles.subtle, fontSize: 11 }}>
                {bot.candidate_count} candidate{bot.candidate_count !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ ...styles.faint, fontSize: 11 }}>{fmtRelative(bot.created_at)}</div>
          {bot.bot_telegram_id != null && (
            <div style={{ ...styles.faint, fontSize: 10, fontFamily: 'monospace', marginTop: 2 }}>
              {bot.bot_telegram_id}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function AdminBotsPage() {
  const [stats, setStats] = useState<BotStats | null>(null)
  const [loadingStats, setLoadingStats] = useState(true)

  const [bots, setBots] = useState<BotListItem[]>([])
  const [total, setTotal] = useState(0)
  const [loadingList, setLoadingList] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)

  const [search, setSearch] = useState('')
  const [searchDebounce, setSearchDebounce] = useState('')
  const [statusFilter, setStatusFilter] = useState<BotStatus | 'ALL'>('ALL')

  useEffect(() => {
    setLoadingStats(true)
    getBotStats(30)
      .then(setStats)
      .catch(() => {})
      .finally(() => setLoadingStats(false))
  }, [])

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounce(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const fetchList = useCallback(async () => {
    setLoadingList(true)
    try {
      const res = await listBots({
        skip: 0,
        search: searchDebounce || undefined,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
      })
      setBots(res.items)
      setTotal(res.total)
    } finally {
      setLoadingList(false)
    }
  }, [searchDebounce, statusFilter])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  const loadMore = async () => {
    setLoadingMore(true)
    try {
      const res = await listBots({
        skip: bots.length,
        search: searchDebounce || undefined,
        status: statusFilter === 'ALL' ? undefined : statusFilter,
      })
      setBots((prev) => [...prev, ...res.items])
      setTotal(res.total)
    } finally {
      setLoadingMore(false)
    }
  }

  const adoption =
    stats && stats.total_merchants > 0
      ? Math.round((stats.active_total / stats.total_merchants) * 1000) / 10
      : 0

  return (
    <div className="p-4 space-y-5" style={{ paddingBottom: 80 }}>
      {/* ── Summary KPIs ── */}
      {!loadingStats && stats && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
            <Kpi label="Active bots" value={fmtNum(stats.active_total)} icon="🤖" tone="green" />
            <Kpi
              label="Adoption"
              value={`${adoption}%`}
              sub={`of ${fmtNum(stats.total_merchants)} shops`}
            />
            <Kpi label="Channel-linked" value={fmtNum(stats.channel_linked_count)} icon="📢" tone="blue" />
            <Kpi label="Connected today" value={fmtNum(stats.connected_today)} sub="new today" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
            <Kpi label="Managed" value={fmtNum(stats.managed_count)} sub="BotFather flow" tone="purple" />
            <Kpi label="Own token" value={fmtNum(stats.byo_count)} sub="merchant-supplied" tone="teal" />
            <Kpi label="Candidates" value={fmtNum(stats.candidates_total)} sub="channels detected" />
          </div>

          {/* Status breakdown */}
          <div style={styles.card}>
            <div style={styles.sectionTitle}>Status</div>
            <StatusBreakdown byStatus={stats.by_status} />
          </div>

          {/* New connections sparkline */}
          {stats.daily.length > 1 && (
            <div style={styles.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                <div style={styles.sectionTitle}>New connections</div>
                <div style={{ ...styles.subtle, fontSize: 11 }}>last {stats.period_days}d</div>
              </div>
              <Sparkline data={stats.daily} />
            </div>
          )}
        </>
      )}

      {/* ── List controls ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search bot, shop, channel…" />
        <PillGroup<BotStatus | 'ALL'>
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'ALL', label: 'All' },
            { value: 'ACTIVE', label: 'Active' },
            { value: 'PENDING', label: 'Pending' },
            { value: 'REVOKED', label: 'Revoked' },
            { value: 'FAILED', label: 'Failed' },
          ]}
        />
      </div>

      {/* ── List ── */}
      {loadingList ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{ ...styles.card, height: 72, opacity: 0.4, animation: 'pulse 1.5s ease-in-out infinite' }}
            />
          ))}
          <style>{`@keyframes pulse{0%,100%{opacity:.3}50%{opacity:.6}}`}</style>
        </div>
      ) : bots.length === 0 ? (
        <EmptyState
          emoji="🤖"
          title="No bots found"
          subtitle={search || statusFilter !== 'ALL' ? 'Try a different filter' : 'No merchant bots connected yet'}
        />
      ) : (
        <>
          <div style={{ ...styles.subtle, fontSize: 11 }}>
            {total} bot{total !== 1 ? 's' : ''}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {bots.map((b) => (
              <BotRow key={b.id} bot={b} />
            ))}
          </div>
          {bots.length < total && (
            <button
              onClick={loadMore}
              disabled={loadingMore}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 12,
                border: 'none',
                cursor: loadingMore ? 'default' : 'pointer',
                background: tokens.cardBg,
                color: tokens.link,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {loadingMore ? 'Loading…' : `Load more (${total - bots.length} remaining)`}
            </button>
          )}
        </>
      )}
    </div>
  )
}
